import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as ArrowIcon } from 'src/assets/images/ArrowRight.svg';
import { ReactComponent as DropdownArrowIcon } from 'src/assets/images/Arrow-Down.svg';
import { PaginationWrapper, PaginationButton, EllipsisSpan, EllipsisDropdown, EllipsisItem } from './styled';

type EllipsisKey = '...-left' | '...-right';
type PageToken = number | EllipsisKey;

interface IPagination {
  nPages: number;
  currentPage?: number; // kept for compatibility; URL is the source of truth
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
  urlKey?: string; // defaults to "page"
}

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

export const Pagination: FC<IPagination> = ({ nPages, setCurrentPage, urlKey = 'page' }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the single source of truth
  const pageFromUrl = parseInt(searchParams.get(urlKey) || '1', 10);
  const page = clamp(isNaN(pageFromUrl) ? 1 : pageFromUrl, 1, Math.max(1, nPages));

  const [showDropdown, setShowDropdown] = useState<null | EllipsisKey>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (setCurrentPage) setCurrentPage(page);
  }, [page, setCurrentPage]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updatePage = (p: number) => {
    setSearchParams({ [urlKey]: String(p) });
    setShowDropdown(null);
  };

  const handlePrevClick = () => {
    if (page > 1) updatePage(page - 1);
  };

  const handleNextClick = () => {
    if (page < nPages) updatePage(page + 1);
  };

  // Build display tokens with explicit first/last-page patterns
  const pageTokens: PageToken[] = useMemo(() => {
    if (nPages <= 1) return [1];

    // First page: 1 2 3 ... n (if enough pages exist)
    if (page === 1) {
      if (nPages === 2) return [1, 2];
      if (nPages === 3) return [1, 2, 3];
      if (nPages === 4) return [1, 2, 3, 4];
      return [1, 2, 3, '...-right', nPages];
    }

    // Last page: 1 ... n-2 n-1 n (if enough pages exist)
    if (page === nPages) {
      if (nPages === 2) return [1, 2];
      if (nPages === 3) return [1, 2, 3];
      if (nPages === 4) return [1, 2, 3, 4];
      return [1, '...-left', nPages - 2, nPages - 1, nPages];
    }

    // Middle pages: standard compact pattern around current page
    const tokens: PageToken[] = [1];

    // left side
    if (page > 3) {
      if (page > 4) tokens.push('...-left');
      else tokens.push(2);
    }
    if (page > 2) tokens.push(page - 1);

    // current
    if (page !== 1 && page !== nPages) tokens.push(page);

    // right side
    if (page < nPages - 1) tokens.push(page + 1);
    if (page < nPages - 2) {
      if (page < nPages - 3) tokens.push('...-right');
      else tokens.push(nPages - 1);
    }

    tokens.push(nPages);

    // de-dupe just in case
    return Array.from(new Set(tokens));
  }, [nPages, page]);

  const getHiddenRange = (side: EllipsisKey): number[] => {
    if (side === '...-left') {
      // Hidden: 2 .. (page - 2)
      const count = Math.max(0, page - 3);
      return Array.from({ length: count }, (_, i) => i + 2);
    }
    // side === "...-right"
    // Hidden: (page + 2) .. (nPages - 1)
    const count = Math.max(0, nPages - page - 2);
    return Array.from({ length: count }, (_, i) => page + i + 2);
  };

  const renderToken = (token: PageToken) => {
    if (typeof token === 'number') {
      return (
        <PaginationButton
          key={token}
          $isActive={page === token}
          onClick={() => updatePage(token)}
          text={token}
          aria-current={page === token ? 'page' : undefined}
          aria-label={`Page ${token}`}
        />
      );
    }

    const side: EllipsisKey = token;
    const candidatePages = getHiddenRange(side);

    // If exactly one page is hidden, render the page instead of ellipsis
    if (candidatePages.length === 1) {
      const onlyPage = candidatePages[0];
      return (
        <PaginationButton
          key={`single-${side}-${onlyPage}`}
          $isActive={page === onlyPage}
          onClick={() => updatePage(onlyPage)}
          text={onlyPage}
          aria-current={page === onlyPage ? 'page' : undefined}
          aria-label={`Page ${onlyPage}`}
        />
      );
    }

    const isOpen = showDropdown === side;

    return (
      <EllipsisSpan
        key={side}
        role="button"
        tabIndex={0}
        aria-label={side === '...-left' ? 'Open left pages' : 'Open right pages'}
        onClick={() => setShowDropdown(isOpen ? null : side)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowDropdown(isOpen ? null : side);
          }
        }}
      >
        {isOpen ? (
          <DropdownArrowIcon className="dropdown-arrow" style={{ width: '1.5rem', height: '1.5rem' }} />
        ) : (
          '...'
        )}
        {isOpen && (
          <EllipsisDropdown $count={candidatePages.length}>
            {candidatePages.map((hiddenPage) => (
              <EllipsisItem key={hiddenPage} onClick={() => updatePage(hiddenPage)}>
                {hiddenPage}
              </EllipsisItem>
            ))}
          </EllipsisDropdown>
        )}
      </EllipsisSpan>
    );
  };

  return (
    <PaginationWrapper ref={wrapperRef} aria-label="Pagination Navigation">
      <PaginationButton
        $isArrow
        $isDisabled={page === 1}
        onClick={handlePrevClick}
        text={<ArrowIcon style={{ transform: 'rotate(180deg)' }} />}
        aria-label="Previous page"
      />

      {pageTokens.map(renderToken)}

      <PaginationButton
        $isArrow
        $isDisabled={page === nPages}
        onClick={handleNextClick}
        text={<ArrowIcon />}
        aria-label="Next page"
      />
    </PaginationWrapper>
  );
};

export default Pagination;
